import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { 
  ArrowRight, 
  Star, 
  Shield, 
  Clock, 
  Award,
  Bath,
  ChefHat,
  Grid3X3,
  Sparkles,
  ChevronLeft,
  ChevronRight,
  Quote,
  Wrench,
  Calendar,
  FileText,
  CreditCard,
  MessageSquare,
  Camera,
  UserCircle,
  CheckCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HomePage = () => {
  const [projects, setProjects] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [currentReview, setCurrentReview] = useState(0);
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [projectsRes, reviewsRes] = await Promise.all([
          axios.get(`${API}/projects?featured=true`),
          axios.get(`${API}/reviews`)
        ]);
        setProjects(projectsRes.data.slice(0, 4));
        setReviews(reviewsRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, []);

  const services = [
    {
      icon: ChefHat,
      title: "Kitchen Tiling",
      description: "Transform your kitchen with stunning wall and floor tiles, splashbacks, and worktop surrounds.",
      image: "https://customer-assets.emergentagent.com/job_tiling-calculator/artifacts/7b3cl7yx_Splashback%20tiling.JPG",
      link: "/estimate",
      cta: "Start My Estimate"
    },
    {
      icon: Bath,
      title: "Bathroom Tiling",
      description: "Create your perfect bathroom with waterproof, beautiful tiles for walls, floors, and wet rooms.",
      image: "https://customer-assets.emergentagent.com/job_tiling-calculator/artifacts/42w9spxp_Hexagon%20tiling%20bathroom.JPG",
      link: "/estimate",
      cta: "Start My Estimate"
    },
    {
      icon: Grid3X3,
      title: "Floor Tiling",
      description: "Durable, stunning floor tiles for hallways, living spaces, conservatories, and more.",
      image: "https://customer-assets.emergentagent.com/job_tiling-calculator/artifacts/hvgewxd3_Victorian%20floor%20tiles.JPG",
      link: "/estimate",
      cta: "Start My Estimate",
      featured: true
    },
    {
      icon: Wrench,
      title: "Bathroom Refits & Renovations",
      description: "Complete bathroom renovations including tiling, plumbing, fixtures, and finishing.",
      image: "https://customer-assets.emergentagent.com/job_tiling-calculator/artifacts/dnlvpa0u_Bathroom.JPG",
      link: "/estimate",
      cta: "Start My Estimate",
      featured: true
    }
  ];

  const trustItems = [
    { icon: Star, value: "5.0", label: "Customer Rating" },
    { icon: Shield, value: "Fully", label: "Insured" },
    { icon: Clock, value: "Free", label: "Quotations" },
    { icon: Award, value: "15+", label: "Years Experience" }
  ];

  const handleSliderMove = (e) => {
    if (!isDragging) return;
    const container = e.currentTarget;
    const rect = container.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setSliderPosition(percentage);
  };

  const handleTouchMove = (e) => {
    const container = e.currentTarget;
    const rect = container.getBoundingClientRect();
    const x = e.touches[0].clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setSliderPosition(percentage);
  };

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="py-12 sm:py-16 md:py-20 lg:py-24 grid-pattern" data-testid="hero-section">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="animate-slide-up text-center lg:text-left mx-auto lg:mx-0 max-w-xl">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-4 sm:mb-6 font-montserrat">
                Precision Tiling for{" "}
                <span className="text-primary">Kitchens, Bathrooms & Floors</span>
              </h1>
              <p className="text-base sm:text-lg md:text-xl text-gray-600 mb-6 sm:mb-8 leading-relaxed">
                Professional installations and reliable quotes from experienced local tilers. Serving Greater Manchester and surrounding areas.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start">
                <Link to="/estimate">
                  <Button 
                    size="lg" 
                    className="bg-primary hover:bg-primary/90 text-white text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 shadow-lg shadow-primary/20 w-full sm:w-auto"
                    data-testid="hero-cta-primary"
                  >
                    Start My Estimate
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Link to="/book">
                  <Button 
                    variant="outline" 
                    size="lg" 
                    className="border-2 border-primary text-primary hover:bg-primary/10 text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 w-full sm:w-auto"
                    data-testid="hero-book-quote"
                  >
                    <Calendar className="w-5 h-5 mr-2" />
                    Book a Quote
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative hidden lg:block">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://customer-assets.emergentagent.com/job_tiling-calculator/artifacts/82pp4ivu_Bathroom2%20.JPG"
                  alt="Modern tiled bathroom by DC Tiling"
                  className="w-full h-full object-cover"
                  data-testid="hero-image"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-4 flex items-center gap-3">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <Shield className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Fully Insured</p>
                  <p className="text-sm text-gray-500">Peace of mind guaranteed</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Strip */}
      <section className="py-12 bg-white border-y border-gray-100" data-testid="trust-strip">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {trustItems.map((item, index) => (
              <div key={index} className="trust-item">
                <div className="trust-icon">
                  <item.icon className="w-6 h-6" />
                </div>
                <p className="font-bold text-2xl text-gray-900">{item.value}</p>
                <p className="text-gray-500">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section-padding bg-section" data-testid="services-section">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 font-montserrat">
              Our Tiling Services
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Expert installation for every room in your home
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <Link 
                to={service.link} 
                key={index}
                className={`service-card ${service.featured ? 'border-primary border-2' : ''}`}
                data-testid={`service-card-${index}`}
              >
                <div className="aspect-[4/3] rounded-lg overflow-hidden mb-4">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="service-card-icon">
                  <service.icon className="w-6 h-6" />
                </div>
                <h3 className="font-semibold text-lg text-gray-900 mb-2 font-montserrat">
                  {service.title}
                </h3>
                <p className="text-gray-600 text-sm mb-4">
                  {service.description}
                </p>
                <span className={`font-medium text-sm flex items-center gap-1 group-hover:gap-2 transition-all ${service.featured ? 'text-primary' : 'text-primary'}`}>
                  {service.cta}
                  <ArrowRight className="w-4 h-4" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Customer Portal Section */}
      <section className="section-padding" data-testid="customer-portal-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-4">
                <UserCircle className="w-4 h-4" />
                <span className="text-sm font-medium">For Our Customers</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 font-montserrat">
                Your Personal Project Portal
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Once your job is booked, you'll receive access to a secure customer portal where you can manage every aspect of your project in one place.
              </p>
              
              <div className="grid sm:grid-cols-2 gap-4 mb-8">
                <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <FileText className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Quotes & Details</h4>
                    <p className="text-sm text-gray-600">View your quotes and project specifications</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Calendar className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Appointments</h4>
                    <p className="text-sm text-gray-600">See appointment dates and job progress</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Camera className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Upload Photos</h4>
                    <p className="text-sm text-gray-600">Share photos and notes with the team</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <CreditCard className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Invoices & Payments</h4>
                    <p className="text-sm text-gray-600">Review invoices and make secure payments</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl sm:col-span-2">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MessageSquare className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Direct Communication</h4>
                    <p className="text-sm text-gray-600">Message the team directly through your portal</p>
                  </div>
                </div>
              </div>
              
              <Link to="/customer-login">
                <Button 
                  size="lg"
                  className="bg-primary hover:bg-primary/90"
                  data-testid="customer-portal-btn"
                >
                  Customer Portal Login
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
            
            <div className="relative hidden lg:block">
              <div className="bg-gradient-to-br from-primary/5 to-primary/10 rounded-2xl p-8">
                <div className="bg-white rounded-xl shadow-lg p-6 mb-4">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                      <UserCircle className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Welcome back, Sarah</p>
                      <p className="text-sm text-gray-500">Bathroom Renovation Project</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="text-sm text-gray-600">Project Status</span>
                      <span className="text-sm font-medium text-green-600 flex items-center gap-1">
                        <CheckCircle className="w-4 h-4" /> In Progress
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="text-sm text-gray-600">Next Appointment</span>
                      <span className="text-sm font-medium text-gray-900">Mon, 15 Dec</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="text-sm text-gray-600">Balance Due</span>
                      <span className="text-sm font-medium text-gray-900">£1,250.00</span>
                    </div>
                  </div>
                </div>
                <p className="text-center text-sm text-gray-500">Example customer portal view</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Recent Projects */}
      <section className="section-padding" data-testid="projects-section">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 font-montserrat">
              Recent Projects
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              See the quality of our work in homes across Greater Manchester
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {projects.map((project, index) => (
              <Link 
                to={`/projects/${project.id}`} 
                key={project.id}
                className="project-card aspect-[4/3]"
                data-testid={`project-card-${index}`}
              >
                <img
                  src={project.images?.[0] || "https://images.unsplash.com/photo-1758548157126-e4c0477f796e?w=600"}
                  alt={project.title}
                  className="w-full h-full object-cover"
                />
                <div className="project-card-overlay" />
                <div className="project-card-content">
                  <h3 className="font-semibold text-lg mb-1">{project.title}</h3>
                  <p className="text-white/80 text-sm">{project.location}</p>
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center">
            <Link to="/quote">
              <Button 
                size="lg"
                className="bg-primary hover:bg-primary/90"
                data-testid="projects-cta"
              >
                Get a Quote For Similar Project
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Before/After Section */}
      <section className="section-padding bg-section" data-testid="before-after-section">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 font-montserrat">
              Before & After Transformations
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Drag the slider to see the incredible transformation
            </p>
          </div>

          <div className="max-w-3xl mx-auto">
            <div 
              className="before-after-container aspect-[4/3] cursor-ew-resize"
              onMouseMove={handleSliderMove}
              onMouseDown={() => setIsDragging(true)}
              onMouseUp={() => setIsDragging(false)}
              onMouseLeave={() => setIsDragging(false)}
              onTouchMove={handleTouchMove}
              data-testid="before-after-slider"
            >
              {/* After Image (Background) */}
              <img
                src="https://customer-assets.emergentagent.com/job_51acb0ce-cb70-4af2-b8f7-b6352ad7d033/artifacts/11mltz9z_After.JPG"
                alt="After renovation by DC Tiling"
                className="absolute inset-0 w-full h-full object-cover"
                data-testid="after-image"
              />
              
              {/* Before Image (Clipped) */}
              <div 
                className="absolute inset-0 overflow-hidden"
                style={{ width: `${sliderPosition}%` }}
              >
                <img
                  src="https://customer-assets.emergentagent.com/job_51acb0ce-cb70-4af2-b8f7-b6352ad7d033/artifacts/2yk8xhgx_Before.JPG"
                  alt="Before renovation"
                  className="absolute inset-0 w-full h-full object-cover"
                  style={{ width: `${100 / (sliderPosition / 100)}%`, maxWidth: "none" }}
                  data-testid="before-image"
                />
              </div>

              {/* Slider Line */}
              <div 
                className="absolute top-0 bottom-0 w-1 bg-white shadow-lg cursor-ew-resize z-10"
                style={{ left: `${sliderPosition}%`, transform: "translateX(-50%)" }}
              >
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center">
                  <ChevronLeft className="w-4 h-4 text-gray-600" />
                  <ChevronRight className="w-4 h-4 text-gray-600" />
                </div>
              </div>

              {/* Labels */}
              <div className="absolute top-4 left-4 bg-black/50 text-white px-3 py-1 rounded-full text-sm font-medium">
                Before
              </div>
              <div className="absolute top-4 right-4 bg-primary text-white px-3 py-1 rounded-full text-sm font-medium">
                After
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section className="section-padding" data-testid="reviews-section">
        <div className="container-custom">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-1 mb-4">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-6 h-6 text-yellow-400 fill-yellow-400" />
              ))}
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 font-montserrat">
              What Our Customers Say
            </h2>
            <p className="text-lg text-gray-600">
              5.0 average rating from 50+ verified customer reviews
            </p>
          </div>

          {reviews.length > 0 && (
            <div className="max-w-3xl mx-auto">
              <div className="review-card text-center relative">
                <Quote className="w-12 h-12 text-primary/20 mx-auto mb-6" />
                <p className="text-xl text-gray-700 mb-6 leading-relaxed">
                  "{reviews[currentReview]?.text}"
                </p>
                <div className="flex items-center justify-center gap-1 mb-4">
                  {[...Array(reviews[currentReview]?.rating || 5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <p className="font-semibold text-gray-900">
                  {reviews[currentReview]?.author}
                </p>
                
                {/* Navigation */}
                <div className="flex items-center justify-center gap-4 mt-6">
                  <button
                    onClick={() => setCurrentReview(prev => (prev === 0 ? reviews.length - 1 : prev - 1))}
                    className="w-10 h-10 rounded-full border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-colors"
                    data-testid="review-prev"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <div className="flex gap-2">
                    {reviews.map((_, i) => (
                      <button
                        key={i}
                        onClick={() => setCurrentReview(i)}
                        className={`w-2 h-2 rounded-full transition-colors ${
                          i === currentReview ? "bg-primary" : "bg-gray-300"
                        }`}
                      />
                    ))}
                  </div>
                  <button
                    onClick={() => setCurrentReview(prev => (prev === reviews.length - 1 ? 0 : prev + 1))}
                    className="w-10 h-10 rounded-full border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-colors"
                    data-testid="review-next"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="text-center mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/estimate">
              <Button 
                size="lg"
                className="bg-primary hover:bg-primary/90"
                data-testid="reviews-cta"
              >
                Start My Estimate
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/quote">
              <Button 
                size="lg"
                variant="outline"
                className="border-2 border-primary text-primary hover:bg-primary/10"
                data-testid="reviews-book-quote"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Book a Quote
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Service Area Section */}
      <section className="section-padding bg-section" data-testid="service-area-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 font-montserrat">
                Serving Greater Manchester
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Serving Greater Manchester and surrounding areas including:
              </p>
              <ul className="grid grid-cols-2 gap-3 mb-8">
                {["Manchester", "Salford", "Stockport", "Trafford", "Bolton", "Bury", "Oldham", "Rochdale", "Tameside", "Wigan"].map((area) => (
                  <li key={area} className="flex items-center gap-2 text-gray-700">
                    <div className="w-2 h-2 bg-primary rounded-full" />
                    {area}
                  </li>
                ))}
              </ul>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/estimate">
                  <Button className="bg-primary hover:bg-primary/90" data-testid="service-area-cta">
                    Start My Estimate
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/book">
                  <Button variant="outline" className="border-2 border-primary text-primary hover:bg-primary/10" data-testid="service-area-book">
                    <Calendar className="w-4 h-4 mr-2" />
                    Book a Quote
                  </Button>
                </Link>
              </div>
            </div>
            <div className="aspect-[4/3] rounded-xl overflow-hidden bg-gray-200">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d152515.87654752788!2d-2.3795674!3d53.4723272!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x487a4d4c5226f5db%3A0xd9be143804fe6baa!2sGreater%20Manchester!5e0!3m2!1sen!2suk!4v1700000000000!5m2!1sen!2suk"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Service Area Map - Greater Manchester"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Lead Funnel CTA Section */}
      <section className="section-padding bg-primary grid-pattern" data-testid="cta-section">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 font-montserrat">
            Get Your Free Estimate in 60 Seconds
          </h2>
          <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
            Choose your project type and get an instant estimate. 
            No obligation, completely free.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/estimate">
              <Button 
                size="lg"
                className="bg-white text-primary hover:bg-gray-100 text-lg px-10 py-6"
                data-testid="final-cta"
              >
                Start My Estimate
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/quote">
              <Button 
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white/10 text-lg px-10 py-6"
                data-testid="final-book-quote"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Book a Quote
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
